/**
 * Blinds Configurator PDP — Horizon-compatible custom element
 */
(function () {
  class BlindsConfiguratorPdp extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.isAddingToCart = false;
      this.lastDisplayedPriceText = '';
      this.variants = [];
      this.variantsByKey = {};
      this.variantsByCode = {};
      this.formData = {
        widthMm: null,
        heightMm: 1800,
        productCode: null,
        variantId: null,
        priceCents: null
      };

      this.form = this.querySelector('[data-bcpdp-form]');
      if (!this.form) return;

      this.shopCurrency = this.form.dataset.shopCurrency || 'ZAR';
      this.shopDomain = (this.form.dataset.shopDomain || '').trim();
      this.minWidthMm = parseInt(this.form.dataset.minWidthMm, 10) || 600;
      this.maxWidthMm = parseInt(this.form.dataset.maxWidthMm, 10) || 3000;
      this.fixedHeightMm = parseInt(this.form.dataset.fixedHeightMm, 10) || 1800;
      this.productHandle = this.form.dataset.productHandle || '';
      this.productCodeOptionName = (this.form.dataset.productCodeOptionName || 'Product Code').toLowerCase();
      this.colourOptionName = (this.form.dataset.colourOptionName || 'Colour').toLowerCase();
      this.storefrontAccessToken = (this.form.dataset.storefrontAccessToken || '').trim();
      this.expectedVariantsCount = parseInt(this.form.dataset.variantsCount, 10) || 0;
      this.catalogSource = '';
      this.catalogWarning = '';
      this.formData.heightMm = this.fixedHeightMm;

      this.loadVariantsFromEmbed();
      this.bindWidthInput();
      this.bindFormSubmit();
      this.bindSuccessActions();
      // Default width (e.g. 600mm) → show price on load
      if (this.widthInput && this.widthInput.value) {
        this.parseWidthInput();
      }
      this.updatePrice();
      this.updateAddToCartState();

      // Liquid/product.js cap at ~250 variants — Storefront API pages through all (needed for S3000 etc.)
      this.loadFullVariantsCatalog().then(() => {
        this.updateCatalogNote();
        this.updatePrice();
        this.updateAddToCartState();
      });
    }

    loadVariantsFromEmbed() {
      const script = this.querySelector('[data-bcpdp-variants]');
      if (!script) return;
      try {
        const data = JSON.parse(script.textContent || '[]');
        if (Array.isArray(data) && data.length) {
          this.applyVariantEntries(data);
          this.catalogSource = 'liquid-embed';
        }
      } catch (e) {
        console.error('Blinds configurator: error parsing embedded variants', e);
      }
    }

    async loadFullVariantsCatalog() {
      const handle = this.resolveProductHandle();
      if (!handle) return false;

      let bestEntries = this.variants.slice();
      let source = bestEntries.length ? 'liquid-embed' : '';
      this.catalogWarning = '';

      if (this.storefrontAccessToken) {
        console.info('Blinds configurator: Storefront API token found — loading all variants…');
        const storefrontResult = await this.fetchAllVariantsStorefront(handle);
        if (storefrontResult?.entries?.length) {
          bestEntries = storefrontResult.entries;
          source = storefrontResult.source;
          this.catalogWarning = '';
        } else if (storefrontResult?.error) {
          this.catalogWarning = storefrontResult.error;
          console.warn('Blinds configurator:', storefrontResult.error);
        } else {
          this.catalogWarning =
            'Storefront API returned no variants. Make sure the product is Active and published to the Online Store (draft products are not available to Storefront API), and that the token has unauthenticated_read_product_listings.';
          console.warn('Blinds configurator:', this.catalogWarning);
        }
      } else {
        this.catalogWarning =
          'Add a Storefront API access token in this block’s settings to price sizes beyond the first ~250 variants (e.g. 3000 mm).';
        // product.js also caps at 250 and 404s for draft/unpublished products — only try when no token
        const jsEntries = await this.fetchVariantsFromProductJs(handle);
        if (jsEntries.length > bestEntries.length) {
          bestEntries = jsEntries;
          source = 'product.js';
        }
      }

      if (bestEntries.length) {
        this.applyVariantEntries(bestEntries);
        this.catalogSource = source;
        console.info(
          `Blinds configurator: loaded ${bestEntries.length} variants via ${source || 'unknown'} (max width ${this.getMaxLoadedWidthMm()} mm)`
        );
        if (this.isCatalogLikelyIncomplete(bestEntries) && !this.catalogWarning) {
          this.catalogWarning =
            'Variant catalog looks incomplete. Publish the product (Active) and set the Storefront API token on this block.';
        }
        return true;
      }
      return false;
    }

    getMaxLoadedWidthMm() {
      return this.variants.reduce((max, e) => Math.max(max, e.widthMm || 0), 0);
    }

    updateCatalogNote() {
      const note = this.querySelector('[data-bcpdp-catalog-note]');
      if (!note) return;
      if (this.catalogWarning && this.isCatalogLikelyIncomplete(this.variants)) {
        note.textContent = this.catalogWarning;
        note.hidden = false;
      } else {
        note.textContent = '';
        note.hidden = true;
      }
    }

    isCatalogLikelyIncomplete(entries) {
      if (!entries || !entries.length) return true;
      const maxWidth = entries.reduce((max, e) => Math.max(max, e.widthMm || 0), 0);
      if (this.expectedVariantsCount > 250 && entries.length <= 250) return true;
      if (this.maxWidthMm >= 2500 && maxWidth < this.maxWidthMm - 50 && entries.length <= 250) {
        return true;
      }
      return false;
    }

    getStorefrontApiUrl() {
      let shop =
        this.shopDomain ||
        window.Shopify?.shop ||
        '';
      if (!shop && /\.myshopify\.com$/i.test(window.location.hostname)) {
        shop = window.location.hostname;
      }
      shop = String(shop || '')
        .trim()
        .replace(/^https?:\/\//i, '')
        .replace(/\/$/, '');
      if (!shop) return null;
      return `https://${shop}/api/2024-10/graphql.json`;
    }

    parseStorefrontVariantGid(gid) {
      if (!gid) return '';
      const match = String(gid).match(/ProductVariant\/(\d+)/);
      return match ? match[1] : '';
    }

    mapStorefrontVariantNode(node) {
      if (!node) return null;
      let code = '';
      let colour = '';
      if (Array.isArray(node.selectedOptions)) {
        node.selectedOptions.forEach((opt) => {
          const name = String(opt.name || '')
            .toLowerCase()
            .trim();
          const value = String(opt.value || '').trim();
          if (name === 'product code' || name === this.productCodeOptionName) {
            code = value.toUpperCase();
          } else if (
            name === 'colour' ||
            name === 'color' ||
            name === this.colourOptionName
          ) {
            colour = value;
          }
        });
      }

      if (!code && Array.isArray(node.selectedOptions) && node.selectedOptions[0]) {
        code = String(node.selectedOptions[0].value || '')
          .toUpperCase()
          .trim();
      }
      if (!colour && Array.isArray(node.selectedOptions) && node.selectedOptions[1]) {
        colour = String(node.selectedOptions[1].value || '').trim();
      }

      const digits = code.replace(/^S/i, '').replace(/\D/g, '');
      const widthMm = parseInt(digits, 10);
      const id = this.parseStorefrontVariantGid(node.id);
      const amount = parseFloat(node.price?.amount);
      if (!id || !code || !widthMm || !Number.isFinite(amount)) return null;

      return {
        code,
        widthMm,
        colour,
        id,
        price: Math.round(amount * 100)
      };
    }

    async fetchAllVariantsStorefront(handle) {
      const token = this.storefrontAccessToken;
      const apiUrl = this.getStorefrontApiUrl();
      if (!token) {
        return { entries: [], error: 'Storefront API token is missing on this block.' };
      }
      if (!apiUrl) {
        return {
          entries: [],
          error: 'Could not resolve shop domain for Storefront API.'
        };
      }

      const query = `
        query ProductVariants($handle: String!, $cursor: String) {
          product(handle: $handle) {
            id
            title
            variants(first: 250, after: $cursor) {
              pageInfo { hasNextPage endCursor }
              edges {
                node {
                  id
                  price { amount }
                  selectedOptions { name value }
                }
              }
            }
          }
        }
      `;

      let cursor = null;
      const allEntries = [];
      let pageCount = 0;
      const maxPages = 20;

      try {
        while (pageCount < maxPages) {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': token
            },
            body: JSON.stringify({
              query,
              variables: { handle, cursor }
            })
          });

          if (!response.ok) {
            return {
              entries: allEntries,
              error: `Storefront API HTTP ${response.status}. Check the access token.`
            };
          }

          const payload = await response.json();
          if (payload.errors && payload.errors.length) {
            return {
              entries: allEntries,
              error: `Storefront API: ${payload.errors[0].message}`
            };
          }

          const product = payload.data?.product;
          if (!product) {
            return {
              entries: [],
              error:
                `Storefront API could not find product "${handle}". Publish it as Active to the Online Store (draft/unpublished products are invisible to Storefront API), then retry.`
            };
          }

          const connection = product.variants;
          if (!connection) break;

          connection.edges.forEach((edge) => {
            const entry = this.mapStorefrontVariantNode(edge.node);
            if (entry) allEntries.push(entry);
          });

          if (!connection.pageInfo?.hasNextPage) break;
          cursor = connection.pageInfo.endCursor;
          pageCount += 1;
        }
      } catch (error) {
        console.warn('Blinds configurator: Storefront API fetch failed', error);
        return {
          entries: allEntries,
          error: `Storefront API request failed: ${error.message || error}`
        };
      }

      if (allEntries.length > 0) {
        return { entries: allEntries, source: 'storefront-graphql' };
      }
      return {
        entries: [],
        error: 'Storefront API returned the product but no parseable Product Code variants.'
      };
    }

    async fetchVariantsFromProductJs(handle) {
      const urls = this.getProductJsonUrls(handle);
      for (const url of urls) {
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (response.status === 404) {
            console.info(
              'Blinds configurator: product.js not available (draft/unpublished). Use Storefront API with an Active published product.'
            );
            return [];
          }
          if (!response.ok) continue;
          const product = await response.json();
          const entries = this.mapProductJsToEntries(product);
          if (entries.length) return entries;
        } catch (e) {
          console.warn('Blinds configurator: product.js fetch failed', url, e);
        }
      }
      return [];
    }

    resolveProductHandle() {
      if (this.productHandle) return this.productHandle;
      const pathMatch = window.location.pathname.match(/\/products\/([^/?#]+)/i);
      if (pathMatch && pathMatch[1]) {
        this.productHandle = decodeURIComponent(pathMatch[1]);
        return this.productHandle;
      }
      const metaProduct = window.Shopify?.analytics?.meta?.product;
      if (metaProduct?.handle) {
        this.productHandle = metaProduct.handle;
        return this.productHandle;
      }
      return '';
    }

    getProductJsonUrls(handle) {
      const encoded = encodeURIComponent(handle);
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      return [...new Set([`${root}/products/${encoded}.js`, `/products/${encoded}.js`])];
    }

    mapProductJsToEntries(product) {
      if (!product || !Array.isArray(product.variants)) return [];

      const optionNames = (product.options || []).map((o) =>
        String(typeof o === 'string' ? o : o?.name || '')
          .toLowerCase()
          .trim()
      );

      let codeIdx = optionNames.findIndex((n) => n === 'product code');
      let colourIdx = optionNames.findIndex((n) => n === 'colour' || n === 'color');
      if (codeIdx < 0) codeIdx = 0;
      if (colourIdx < 0) colourIdx = optionNames.length > 1 ? 1 : -1;

      return product.variants
        .map((variant) => {
          const options = variant.options || [];
          const codeRaw = options[codeIdx] || variant.option1 || '';
          const colour = colourIdx >= 0 ? options[colourIdx] || '' : variant.option2 || '';
          const code = String(codeRaw || '')
            .toUpperCase()
            .trim();
          const digits = code.replace(/^S/i, '').replace(/\D/g, '');
          const widthMm = parseInt(digits, 10);
          if (!code || !widthMm || !variant.id) return null;

          let price = variant.price;
          if (typeof price === 'string' && price.includes('.')) {
            price = Math.round(parseFloat(price) * 100);
          } else {
            price = Number(price) || 0;
          }

          return {
            code,
            widthMm,
            colour: String(colour || ''),
            id: String(variant.id),
            price
          };
        })
        .filter(Boolean);
    }

    applyVariantEntries(entries) {
      this.variants = entries.filter((v) => v && v.id && v.code);
      this.variantsByKey = {};
      this.variantsByCode = {};
      this.variants.forEach((v) => {
        this.variantsByKey[this.variantKey(v.code, v.colour)] = v;
        const code = String(v.code || '').toUpperCase();
        if (code && !this.variantsByCode[code]) {
          this.variantsByCode[code] = v;
        }
      });
    }

    variantKey(code, colour) {
      return `${String(code || '').toUpperCase()}|${String(colour || '').toLowerCase()}`;
    }

    roundWidthUpTo5(widthMm) {
      const n = Math.ceil(Number(widthMm));
      if (!Number.isFinite(n) || n <= 0) return 0;
      return Math.ceil(n / 5) * 5;
    }

    productCodeFromWidthMm(widthMm) {
      const rounded = this.roundWidthUpTo5(widthMm);
      if (!rounded) return null;
      return `S${String(rounded).padStart(4, '0')}`;
    }

    findVariantByCode(code) {
      if (!code) return null;
      return this.variantsByCode[String(code).toUpperCase()] || null;
    }

    bindWidthInput() {
      this.widthInput = this.querySelector('[data-bcpdp-width-input]');
      if (!this.widthInput) return;

      const onChange = () => {
        this.parseWidthInput();
        this.updatePrice();
        this.updateAddToCartState();
      };

      this.widthInput.addEventListener('input', onChange);
      this.widthInput.addEventListener('change', onChange);
      this.widthInput.addEventListener('blur', onChange);
    }

    parseWidthInput() {
      const errorEl = this.querySelector('[data-bcpdp-width-error]');
      const raw = (this.widthInput.value || '').trim().replace(/,/g, '');
      const widthMm = parseInt(raw, 10);

      this.widthInput.classList.remove('bcpdp-dimension__field--invalid');
      if (errorEl) errorEl.hidden = true;

      if (!raw || !Number.isFinite(widthMm)) {
        this.formData.widthMm = null;
        this.formData.productCode = null;
        this.formData.variantId = null;
        this.formData.priceCents = null;
        return false;
      }

      if (widthMm < this.minWidthMm || widthMm > this.maxWidthMm) {
        this.formData.widthMm = null;
        this.formData.productCode = null;
        this.formData.variantId = null;
        this.formData.priceCents = null;
        this.widthInput.classList.add('bcpdp-dimension__field--invalid');
        if (errorEl) {
          errorEl.textContent = `Please enter a width between ${this.minWidthMm} and ${this.maxWidthMm} mm.`;
          errorEl.hidden = false;
        }
        return false;
      }

      this.formData.widthMm = widthMm;
      this.formData.productCode = this.productCodeFromWidthMm(widthMm);
      return true;
    }

    updatePrice() {
      const priceEl = this.querySelector('[data-bcpdp-live-price]');
      if (!priceEl) return;

      if (!this.formData.widthMm || !this.formData.productCode) {
        this.formData.variantId = null;
        this.formData.priceCents = null;
        priceEl.textContent = '-';
        this.lastDisplayedPriceText = '-';
        return;
      }

      const variant = this.findVariantByCode(this.formData.productCode);
      if (!variant) {
        this.formData.variantId = null;
        this.formData.priceCents = null;
        const loaded = this.variants.length;
        if (loaded === 0) {
          priceEl.textContent = 'Loading price…';
        } else if (
          !this.storefrontAccessToken &&
          this.formData.widthMm > 1000 &&
          this.isCatalogLikelyIncomplete(this.variants)
        ) {
          priceEl.textContent = 'Add Storefront API token for this size';
        } else {
          priceEl.textContent = 'Size not available';
        }
        this.lastDisplayedPriceText = priceEl.textContent;
        return;
      }

      this.formData.variantId = String(variant.id);
      this.formData.priceCents = Number(variant.price) || 0;
      const text = this.formatMoney(this.formData.priceCents / 100);

      if (text !== this.lastDisplayedPriceText) {
        this.animatePriceChange(priceEl, text);
      }
    }

    animatePriceChange(element, text) {
      if (!element) return;
      element.classList.remove('is-price-animating');
      // Force reflow so the animation restarts (matches curtain PDP)
      void element.offsetWidth;
      element.textContent = text;
      element.classList.add('is-price-animating');
      this.lastDisplayedPriceText = text;
    }

    formatMoney(amount) {
      const value = Number(amount);
      if (!Number.isFinite(value)) return '-';
      try {
        return new Intl.NumberFormat('en-ZA', {
          style: 'currency',
          currency: this.shopCurrency || 'ZAR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value);
      } catch (e) {
        return `R ${value.toFixed(2)}`;
      }
    }

    isReadyToAdd() {
      return !!(
        this.formData.widthMm &&
        this.formData.variantId &&
        this.formData.productCode
      );
    }

    updateAddToCartState() {
      const btn = this.querySelector('[data-bcpdp-add-to-cart]');
      if (!btn) return;
      const ready = this.isReadyToAdd();
      btn.disabled = !ready;
      btn.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }

    bindFormSubmit() {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    bindSuccessActions() {
      const continueBtn = this.querySelector('[data-bcpdp-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', () => this.resetForm());
      }
    }

    async addToCart() {
      if (this.isAddingToCart) return;

      this.parseWidthInput();
      this.updatePrice();

      if (!this.isReadyToAdd()) {
        this.showError('Please enter a valid width.');
        this.updateAddToCartState();
        return;
      }

      this.isAddingToCart = true;
      const addToCartBtn = this.querySelector('[data-bcpdp-add-to-cart]');
      const btnText = addToCartBtn ? addToCartBtn.querySelector('.btn__text') : null;
      const btnLoader = addToCartBtn ? addToCartBtn.querySelector('.btn__loader') : null;

      if (addToCartBtn) addToCartBtn.disabled = true;
      if (btnText) btnText.style.display = 'none';
      if (btnLoader) btnLoader.style.display = 'inline-flex';

      try {
        const rounded = this.roundWidthUpTo5(this.formData.widthMm);
        const properties = {
          Width: `${this.formData.widthMm} mm`,
          Height: `${this.formData.heightMm} mm`,
          'Product code': this.formData.productCode,
          '_Entered Width (mm)': String(this.formData.widthMm),
          '_Rounded Width (mm)': String(rounded)
        };

        const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: this.formData.variantId,
                quantity: 1,
                properties
              }
            ]
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.description || 'Failed to add item to cart');
        }

        const cart = await this.fetchCartResource();
        this.showSuccess();
        await this.notifyCartUpdated({ cart, itemCount: cart?.item_count });
      } catch (error) {
        console.error('Error adding blinds to cart:', error);
        this.showError(
          error.message || 'An error occurred while adding the item to your cart. Please try again.'
        );
      } finally {
        this.isAddingToCart = false;
        if (btnText) btnText.style.display = 'inline';
        if (btnLoader) btnLoader.style.display = 'none';
        this.updateAddToCartState();
      }
    }

    async fetchCartResource() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (e) {
        return null;
      }
    }

    async notifyCartUpdated(options = {}) {
      const cart = options.cart || (await this.fetchCartResource());
      const detail = {
        cart,
        itemCount: options.itemCount != null ? options.itemCount : cart?.item_count
      };
      document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail }));
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, detail);
      }
    }

    showSuccess() {
      const successMessage = this.querySelector('[data-bcpdp-success]');
      if (successMessage && this.form) {
        this.form.style.display = 'none';
        successMessage.style.display = 'block';
        successMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    showError(message) {
      const errorMessage = this.querySelector('[data-bcpdp-error]');
      const errorText = this.querySelector('[data-bcpdp-error-text]');
      if (errorMessage && errorText) {
        errorText.textContent = message;
        errorMessage.style.display = 'block';
        errorMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setTimeout(() => {
          errorMessage.style.display = 'none';
        }, 5000);
      }
    }

    resetForm() {
      this.formData = {
        widthMm: null,
        heightMm: this.fixedHeightMm,
        productCode: null,
        variantId: null,
        priceCents: null
      };

      if (this.widthInput) {
        this.widthInput.value = String(this.minWidthMm);
        this.widthInput.classList.remove('bcpdp-dimension__field--invalid');
        this.parseWidthInput();
      }

      const errorEl = this.querySelector('[data-bcpdp-width-error]');
      if (errorEl) errorEl.hidden = true;

      this.updatePrice();
      this.updateAddToCartState();

      if (this.form) this.form.style.display = 'block';
      const successMessage = this.querySelector('[data-bcpdp-success]');
      if (successMessage) successMessage.style.display = 'none';
      const errorMessage = this.querySelector('[data-bcpdp-error]');
      if (errorMessage) errorMessage.style.display = 'none';
    }
  }

  if (!customElements.get('blinds-configurator-pdp')) {
    customElements.define('blinds-configurator-pdp', BlindsConfiguratorPdp);
  }
})();
